import { underlyingOf } from '@/features/hedge/lib/hedge-instrument'
import { DEFAULT_AMBERDATA_EXCHANGE } from '@/service/amberdata/analytics.types'
import { dayBoundsMs } from '@/service/amberdata/day-bounds'
import { AnalyticsType } from '@/types/generated/common/types'
import type { WidgetContext } from '@/types/generated/research'

const ONE_DAY_MS = 24 * 60 * 60 * 1000

interface DateRange {
  from: string
  to: string
}

export interface BuildExplainWidgetContextInput {
  analyticsType: AnalyticsType
  symbol: string
  isComparison: boolean
  isDateRangePicker: boolean
  isLegacyRange: boolean
  date1: number | null
  ivrRange: DateRange
  legacyRange: DateRange
}

// Pure, so range-derivation per widget mode is unit-testable without rendering the widget.
// nowMs is an explicit argument (not Date.now() called internally) so live/trailing-window
// branches are deterministic in tests — callers should pass Date.now() captured at the
// moment the user actually clicks "explain", not a value memoized earlier during render.
export function buildExplainWidgetContext(
  input: BuildExplainWidgetContextInput,
  nowMs: number
): WidgetContext | null {
  if (input.analyticsType === AnalyticsType.ANALYTICS_TYPE_VOL_METRICS_24HR) {
    // No QuestDB table yet — explainer unsupported for this metric.
    return null
  }

  let rangeStartMs: string
  let rangeEndMs: string

  if (input.isDateRangePicker) {
    rangeStartMs = input.ivrRange.from
    rangeEndMs = input.ivrRange.to
  } else if (input.isComparison) {
    if (input.date1 !== null) {
      const { start, end } = dayBoundsMs(input.date1)
      rangeStartMs = String(start)
      rangeEndMs = String(end)
    } else {
      // Live/current — no explicit reference date, use a trailing 1-day window.
      rangeEndMs = String(nowMs)
      rangeStartMs = String(nowMs - ONE_DAY_MS)
    }
  } else if (input.isLegacyRange) {
    rangeStartMs = input.legacyRange.from
    rangeEndMs = input.legacyRange.to
  } else {
    // No Phase 1 analytics type besides Vol Metrics 24h (handled above) falls here today.
    return null
  }

  return {
    metric: input.analyticsType,
    currency: underlyingOf(input.symbol),
    exchange: DEFAULT_AMBERDATA_EXCHANGE,
    rangeStartMs,
    rangeEndMs,
  }
}
